import { useEffect, useRef, useCallback, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useFlags } from '@highlight/analytics/react'
import { useIsOnboarded } from '@renderer/hooks/useIsOnboarded'
import { useSidepanel } from '@renderer/overlay/atoms'
import { useQuickTourActiveAtom, useStartQuickTour } from '@renderer/overlay/Sidepanel/atoms'
import { dayZeroTourAtom } from '@renderer/overlay/Sidepanel/components/day-zero-tour/tour-state'
import { inTourTestArm } from '@renderer/overlay/Sidepanel/components/day-zero-tour/use-day-zero-tour'
import { toastManager } from '@renderer/overlay/Toaster/toast-store'
// Panel opens mid-dissolve of the onboarding window; the shared module owns
// the choreography and its rationale.
import { HANDOFF_OPEN_DELAY_MS } from '@shared/onboarding/handoff'

/** How long the handoff waits for PostHog flags before falling back to the walkthrough. */
const FLAGS_WAIT_MS = 5000

/**
 * Mounted by the Toaster so the handoff can open the unmounted sidepanel.
 * Dev settings can replay onboarding:windowComplete to restart the quick tour.
 */
export function useOnboardingCompletion() {
  const isOnboarded = useIsOnboarded()
  const [, setSidepanelState] = useSidepanel()
  const startQuickTour = useStartQuickTour()
  const [, setQuickTourActive] = useQuickTourActiveAtom()
  const flags = useFlags({ live: true })
  // Waiting for flags after the handoff, or running the fallback walkthrough until they land.
  const [tourChoice, setTourChoice] = useState<'idle' | 'waiting' | 'fallback'>('idle')
  const { forced } = useAtomValue(dayZeroTourAtom)
  // A developer-forced Day 0 tour shows whatever the arm, so it replaces the walkthrough too.
  const testArm = tourChoice === 'idle' ? null : forced ? true : flags.ready ? inTourTestArm(flags) : null
  const handoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    toastManager.setSuppressed(!isOnboarded)
  }, [isOnboarded])

  useEffect(() => {
    return () => {
      if (handoffTimerRef.current != null) clearTimeout(handoffTimerRef.current)
    }
  }, [])

  const handleOnboardingHandoff = useCallback(() => {
    // Reset to hidden first so re-runs (dev settings) don't start from a
    // panel the prior run's tour left open — the slide-in is the transition.
    setSidepanelState('hidden')
    setTourChoice('waiting')
    if (handoffTimerRef.current != null) clearTimeout(handoffTimerRef.current)
    handoffTimerRef.current = setTimeout(() => {
      setSidepanelState('open')
    }, HANDOFF_OPEN_DELAY_MS)
  }, [setSidepanelState])

  // The Day 0 tour replaces the walkthrough for its test arm, so a fallback walkthrough stops if flags land late.
  useEffect(() => {
    if (tourChoice === 'idle' || testArm === null) {
      return
    }
    if (tourChoice === 'waiting' && !testArm) startQuickTour()
    if (tourChoice === 'fallback' && testArm) setQuickTourActive(false)
    setTourChoice('idle')
  }, [tourChoice, testArm, startQuickTour, setQuickTourActive])

  useEffect(() => {
    if (tourChoice !== 'waiting') {
      return
    }
    const timer = setTimeout(() => {
      startQuickTour()
      setTourChoice('fallback')
    }, FLAGS_WAIT_MS)
    return () => clearTimeout(timer)
  }, [tourChoice, startQuickTour])

  useGlobalEvent('onboarding:windowComplete', handleOnboardingHandoff)
}
